import { useEffect, useState } from 'react';
import { NudosHoverText, NudosSelectButton } from '../../../../../../../../components/NudosComponents';
import { TemporaryStorageDropdown } from './TemporaryStorageDropdown/TemporaryStorageDropdown';
import {
  IOption,
  IShipmentData,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_ID,
  TEMPORARY_STORAGE_REASON_TYPE_ID,
  IproductReferenceDataInCountry
} from '../../../../../../../../types/orders';
import { WarehouseTemporaryStorageModal } from '../WarehouseTemporaryStorageModal/WarehouseTemporaryStorageModal';
import { IWarehouseTemporaryStorageModalCustomSectionInitialValues } from '../WarehouseTemporaryStorageModal/WarehouseTemporaryStorageModalCustomSection';
import { displayErrorNotification } from '../../../../../../../../utils/displayNudosStandardNotifications';
import BordOneToneIcon from '../../../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';

import './WarehouseTemporaryStorageButton.scss';

export const WarehouseTemporaryStorageButton = ({
  shipmentId,
  initialValues,
  reloadDataCallback,
  selectOption,
  shipmentData,
  orderId,
  organizationName,
  receiverName,
  references
}: IWarehouseTemporaryStorageButtonProps) => {
  const [showReasonsDropdown, setShowReasonsDropdown] = useState(false);
  const [reasonsForTemporaryStorage, setReasonsForTemporaryStorage] = useState<TEMPORARY_STORAGE_REASON_TYPE_ID[]>([]);
  const [showTemporaryStorageModal, setShowTemporaryStorageModal] = useState(false);
  const [missingData, setMissingData] = useState(false);

  const enableCheckbox = selectOption?.name === SHIPMENT_STATUS.CONFIRMED;
  const atWarehouseStatus = selectOption?.name === SHIPMENT_STATUS.WAREHOUSE_STOP;
  const showDisabledText =
    (selectOption?.ShipmentStatusId || 0) > SHIPMENT_STATUS_ID.CONFIRMED && !initialValues?.estimatedEntryDate;

  const hasTemporaryStorage = !!initialValues?.estimatedEntryDate;
  const initialReasons = initialValues?.warehouseTemporaryStorageReasons?.map(
    reason => reason.temporaryStorageReasonId
  );

  const atWarehouseCompleted =
    [SHIPMENT_STATUS_ID.DELIVERY_PROCESS, SHIPMENT_STATUS_ID.DELIVERED].includes(selectOption?.ShipmentStatusId || 0) &&
    hasTemporaryStorage;

  const preventReasonsModalOpen = [
    SHIPMENT_STATUS_ID.DELIVERY_PROCESS,
    SHIPMENT_STATUS_ID.DELIVERED,
    SHIPMENT_STATUS_ID.WAREHOUSE_STOP
  ].includes(selectOption?.ShipmentStatusId || 0);

  const currentReasonsLinkText = initialValues?.warehouseTemporaryStorageReasons
    ?.map(reason => reason.temporaryStorageReason.name)
    ?.join(', ')
    ?.trim();

  const handleClickCheckbox = (event: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    setShowReasonsDropdown(!showReasonsDropdown);
  };

  const handleContinue = (reasons: TEMPORARY_STORAGE_REASON_TYPE_ID[]) => {
    setReasonsForTemporaryStorage(reasons);
    setShowTemporaryStorageModal(true);
  };

  const handleClickReasonsLink = () => {
    if (preventReasonsModalOpen) return;
    const initialReasonsIds = initialValues?.warehouseTemporaryStorageReasons?.map(
      reason => reason.temporaryStorageReasonId
    );
    if (!initialReasonsIds?.length) return;
    setReasonsForTemporaryStorage(initialReasonsIds);
    setShowTemporaryStorageModal(true);
  };

  const alertPendingDataForTemporaryStorage = () => {
    if (!initialValues?.warehouseTemporaryStorageReasons?.length) return;
    if (initialValues?.estimatedEntryDate && initialValues?.trackingCode && initialValues?.trackingUrl) return;
    displayErrorNotification({
      customJSXMessage: (
        <div>Agrega información de fecha y tracking con Parada en Bodega en los productos señalados.</div>
      )
    });
    setMissingData(true);
  };

  useEffect(() => {
    const handleClickOutside = () => setShowReasonsDropdown(false);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    alertPendingDataForTemporaryStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues?.estimatedEntryDate,
    initialValues?.trackingCode,
    initialValues?.trackingUrl,
    initialValues?.warehouseTemporaryStorageReasons?.length
  ]);

  return (
    <div className="warehouseTemporaryStorageButton">
      <div className="checkboxButton">
        {!atWarehouseCompleted && !atWarehouseStatus && (
          <NudosSelectButton
            componentSize="xs"
            colorPalette="black"
            handleClickButton={handleClickCheckbox}
            isButtonActive={showReasonsDropdown || hasTemporaryStorage}
            isButtonDisabled={!enableCheckbox}
          />
        )}
        {atWarehouseStatus && (
          <div className="disabledActiveCheckBox">
            <BordOneToneIcon variant="check" customWidth="10px" stroke="white" />
          </div>
        )}
        <p className={showDisabledText ? 'disabledText' : ''}>
          {atWarehouseCompleted ? 'Estuvo en bodega por:' : 'Envío con Parada en Bodega:'}
        </p>
        {currentReasonsLinkText && (
          <span className="reasonsLinkButtonContainer">
            <NudosHoverText
              customClassName={`reasonsLinkButton ${missingData ? 'missingData' : ''} ${
                preventReasonsModalOpen ? 'preventReasonsModalOpen' : ''
              } ${atWarehouseStatus ? 'atWarehouseStatus' : ''}`}
              text={currentReasonsLinkText}
              charactersLimit={missingData ? 27 : 30}
              onlyIfTruncated
              onClick={handleClickReasonsLink}
            />
            {missingData && <span className="missingDataIcon">⚠️</span>}
          </span>
        )}
      </div>
      {showReasonsDropdown && (
        <TemporaryStorageDropdown
          shipmentId={shipmentId}
          continueCallback={handleContinue}
          initialReasons={initialReasons}
          reloadDataCallback={reloadDataCallback}
          closeDropdownCallback={() => setShowReasonsDropdown(false)}
        />
      )}
      {showTemporaryStorageModal && (
        <WarehouseTemporaryStorageModal
          shipmentId={shipmentId}
          initialValues={initialValues}
          reasonsForTemporaryStorage={reasonsForTemporaryStorage}
          closeModalCallback={() => setShowTemporaryStorageModal(false)}
          reloadDataCallback={reloadDataCallback}
          shipmentData={shipmentData}
          orderId={orderId}
          organizationName={organizationName}
          flag={shipmentData?.country?.flag}
          receiverName={receiverName}
          references={references}
        />
      )}
    </div>
  );
};

interface IWarehouseTemporaryStorageButtonProps {
  shipmentId: number;
  initialValues: IWarehouseTemporaryStorageModalCustomSectionInitialValues;
  reloadDataCallback?: () => Promise<void>;
  selectOption?: IOption;
  shipmentData?: IShipmentData;
  orderId?: string;
  organizationName?: string;
  receiverName?: string;
  references?: IproductReferenceDataInCountry[];
}
